import React, { useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import './ParallaxEffectBlock.scss';
import useEvent from 'js/use-event';
import throttle from 'lodash/throttle';

const ParallaxEffectBlock = ({ blocks }) => {
  const containerRef = useRef();
  const itemRefs = useRef(blocks ? blocks.map(() => React.createRef()) : []);

  const getWindowHeight = () =>
    window.innerHeight || document.documentElement.clientHeight;

  const getVisibilityPercent = el => {
    const { top, bottom, height } = el.getBoundingClientRect();
    const windowHeight = getWindowHeight();
    const visibleTop = Math.max(top, 0);
    const visibleBottom = Math.min(bottom, windowHeight);
    const visibleHeight = Math.max(visibleBottom - visibleTop, 0);
    return visibleHeight / height;
  };

  const onScroll = () => {
    const fixedClass = 'parallax-effect-block__block-media_fixed';
    const lastIndex = itemRefs.current.length - 1;

    itemRefs.current.forEach((ref, index) => {
      const item = ref.current;
      if (!item) return;
      const media = item.querySelector('.parallax-effect-block__block-media');

      const { bottom } = item.getBoundingClientRect();
      const windowHeight = getWindowHeight();

      const visibility = getVisibilityPercent(item);

      if (index === lastIndex && bottom <= windowHeight) {
        media.classList.remove(fixedClass);
        media.style.opacity = 1;
        media.style.transition = 'opacity 0.5s ease';
        return;
      }

      if (visibility > 0) {
        media.classList.add(fixedClass);
        media.style.transition = 'opacity 0.5s ease';
        media.style.opacity = visibility > 0.3 ? 1 : 0;
      } else {
        media.classList.remove(fixedClass);
        media.style.opacity = 0;
        media.style.transition = '';
      }
    });
  };

  const throttledScrollHandler = useMemo(() => throttle(onScroll, 50), []);

  let lastScrollY = 0;
  useEvent('scroll', () => {
    const newScrollY = window.scrollY;
    throttledScrollHandler(newScrollY > lastScrollY);
    lastScrollY = newScrollY;
  });

  useEvent('resize', () => {
    throttledScrollHandler(true);
  });

  return (
    <div className="parallax-effect-block" ref={containerRef}>
      {blocks &&
        blocks.map((block, index) => {
          const Block = (typeof window !== 'undefined' ? window : global)
            .Components?.[block.componentName];

          if (!Block) {
            console.error(`Components.${block.componentName} is not defined`);
            return null;
          }

          return (
            <div
              key={index}
              className="parallax-effect-block__item"
              ref={itemRefs.current[index]}
            >
              <Block
                {...block}
                mediaClassName="parallax-effect-block__block-media"
              />
            </div>
          );
        })}
    </div>
  );
};

ParallaxEffectBlock.propTypes = {
  blocks: PropTypes.arrayOf(
    PropTypes.shape({
      componentName: PropTypes.oneOf([
        'HeroBlock',
        'FullPageQuotationBlock',
        'FullPageImageBlock',
      ]),
    })
  ),
};

export default ParallaxEffectBlock;
